import IcNodata from "@/assets/icons/ic-nodata.svg";
import IconPlus from "@/assets/icons/ic-plus.svg";
import IconHelp from "@/assets/icons/icon-help.svg";
import { ConfirmDialog } from "@/components/common/confirm-dialog";
import { SearchInput } from "@/components/common/search-input";
import { Button } from "@/components/ui/button";
import Pagination from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TruncatedTooltip } from "@/components/ui/truncated-tooltip";
import { toast } from "@/hooks/use-toast";
import { usePermissions } from "@/lib/permissions";
import { formatDate } from "@/lib/utils";
import {
  AdminManagementService,
  GetListAdminReq,
  GetListAdminRes,
  IAdminRole,
} from "@/services/admin-management-service";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; padding?: string }> =
  {
    active: {
      label: "Active",
      bg: "bg-[#43946C]",
      text: "text-[#000000]",
      padding: "px-[15px] py-[2px]",
    },
    inactive: {
      label: "Inactive",
      bg: "bg-[#EBEBEB]",
      text: "text-[#000000]",
      padding: "px-[10px] py-[2px]",
    },
  };

const ROLE_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  administrator: {
    label: "Administrator",
    bg: "bg-[rgba(47,34,105,0.06)]",
    text: "text-[#2f2269]",
  },
  editor: {
    label: "Editor",
    bg: "bg-[rgba(0,186,248,0.12)]",
    text: "text-[#083d4a]",
  },
  viewer: {
    label: "Viewer",
    bg: "bg-[rgba(255,91,0,0.12)]",
    text: "text-[#582d19]",
  },
};

const ROLE_FILTER_OPTIONS = [
  { value: "all", label: "전체 역할" },
  { value: "administrator", label: "Administrator" },
  { value: "editor", label: "Editor" },
  { value: "viewer", label: "Viewer" },
];

const PAGE_SIZE = 10;

const AdminManagementListPage = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canCreate = can.create("admin_management");
  const canUpdate = can.update("admin_management");
  const canDelete = can.delete("admin_management");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [roleFilter, setRoleFilter] = useState<IAdminRole | "all">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [admins, setAdmins] = useState<GetListAdminRes[]>([]);
  const [total, setTotal] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const hasFetchedRef = useRef(false);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const fetchAdmins = useCallback(
    async (page: number) => {
      if (hasFetchedRef.current) {
        setIsSearching(true);
      }
      try {
        const params: GetListAdminReq = {
          page,
          page_size: PAGE_SIZE,
        };
        if (searchValue.trim()) {
          params.search = searchValue.trim();
        }
        if (roleFilter !== "all") {
          params.role = roleFilter as IAdminRole;
        }
        const res = await AdminManagementService.getList(params);
        if (res.data?.success && res.data?.data) {
          setAdmins(res.data.data.items);
          setTotal(res.data.data.total);
        }
      } catch {
        setAdmins([]);
        setTotal(0);
      } finally {
        setIsInitialLoading(false);
        setIsSearching(false);
        hasFetchedRef.current = true;
      }
    },
    [searchValue, roleFilter],
  );

  useEffect(() => {
    fetchAdmins(currentPage);
  }, [fetchAdmins, currentPage]);

  // Auto-fetch when search or role filter changes
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, roleFilter]);

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchValue("");
    setCurrentPage(1);
  };

  const handleRoleChange = (value: string) => {
    setRoleFilter(value as IAdminRole | "all");
    setCurrentPage(1);
  };

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const handleDeleteClick = (e: React.MouseEvent, adminId: string) => {
    e.stopPropagation();
    setDeleteTargetId(adminId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await AdminManagementService.deleteAdmin(deleteTargetId);
      toast({ description: "관리자가 삭제되었습니다." });
      fetchAdmins(currentPage);
    } catch {
      toast({
        description: "관리자 삭제에 실패했습니다.",
        variant: "destructive",
      });
    } finally {
      setDeleteTargetId(null);
    }
  };

  const handleEdit = (e: React.MouseEvent, adminId: string) => {
    e.stopPropagation();
    navigate(`/admin-management/${adminId}/edit`);
  };

  return (
    <>
      {isInitialLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div
        className="bg-white   flex flex-col gap-6"
        style={{ display: isInitialLoading ? "none" : "flex" }}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Search */}
            <SearchInput
              placeholder="이름 또는 이메일로 검색..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchValue(e.target.value.trim());
              }}
              onClear={handleClearSearch}
            />

            {/* Role Filter */}
            <Select value={roleFilter} onValueChange={handleRoleChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="전체 역할" />
              </SelectTrigger>
              <SelectContent>
                {ROLE_FILTER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {canCreate && (
            <Button
              onClick={() => navigate("/admin-management/create")}
              variant={"default"}
              className="h-48px font-bold"
            >
              <img src={IconPlus} alt="plus" className="w-5 h-5" />새 관리자
            </Button>
          )}
        </div>

        {/* Record Count */}
        <div className="flex justify-start">
          <span className="text-sm text-[#8D8D8D]">
            전체 {total}명의 관리자 중 {admins.length}명 표시 중
          </span>
        </div>

        {/* Table */}
        <div
          className={`border border-[rgba(0,0,0,0.09)] rounded overflow-x-auto min-w-0 w-full transition-opacity duration-200 ${isSearching ? " pointer-events-none" : ""}`}
        >
          <Table className="min-w-[1050px]">
            <TableHeader>
              <TableRow className="bg-[#EBEBEB] ">
                <TableHead className="w-[300px]">이름</TableHead>
                <TableHead className="w-[152px]">역할</TableHead>
                <TableHead className="w-[121px]">상태</TableHead>
                <TableHead className="w-[162px]">최종 로그인</TableHead>
                <TableHead className="w-[100px] text-right">관리</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((admin) => {
                const statusConfig = STATUS_CONFIG[admin.status] || STATUS_CONFIG.active;
                const roleConfig = ROLE_CONFIG[admin.role] || ROLE_CONFIG.viewer;
                return (
                  <TableRow
                    key={admin.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/admin-management/${admin.id}`)}
                  >
                    <TableCell className="text-[rgba(0,0,0,0.91)] max-w-[300px] truncate px-4 py-[3px]">
                      <div>
                        <TruncatedTooltip className="text-base font-medium text-[#000000e8] truncate">
                          {admin.name}
                        </TruncatedTooltip>
                        <TruncatedTooltip className="text-xs text-[#B0B0B0] truncate">
                          {admin.email}
                        </TruncatedTooltip>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-[3px]">
                      <div className={`inline-flex items-center h-6 px-2 rounded text-[#000000]`}>
                        <span className="text-[16px] font-medium">{roleConfig.label}</span>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-[3px]">
                      <div
                        className={`inline-flex items-center gap-1 h-6  rounded ${statusConfig.bg} ${statusConfig.text} ${statusConfig.padding}`}
                      >
                        <span className="text-sm font-medium">{statusConfig.label}</span>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-[3px] text-base text-[rgba(0,0,0,0.91)] whitespace-nowrap">
                      {admin.last_login_at
                        ? formatDate(admin.last_login_at, "YYYY-MM-DD HH:mm")
                        : "-"}
                    </TableCell>

                    <TableCell className="px-4 py-[3px]">
                      <div className="flex items-center justify-end gap-[8px]">
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-[80px] h-[32px] rounded text-[12px] font-bold text-[#000000] bg-[#F7DDCF]"
                            onClick={(e) => handleDeleteClick(e, admin.id)}
                          >
                            삭제
                          </Button>
                        )}
                        {canUpdate && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-[80px] h-[32px] rounded text-[12px] font-bold text-[#202020] border border-[rgba(0,0,0,0.15)]"
                            onClick={(e) => handleEdit(e, admin.id)}
                          >
                            수정
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {admins.length === 0 && !isInitialLoading && !isSearching && (
                <TableRow>
                  <TableCell colSpan={6} className="h-[600px] text-center">
                    <div className="flex flex-col items-center justify-center h-full gap-4">
                      <img src={IcNodata} alt="No data" className="" />

                      <p className="text-[32px] text-[#000000E8] ">
                        {searchValue || roleFilter !== "all" ? "검색 결과가 없습니다" : "No data"}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {!isInitialLoading && admins.length > 0 && (
          <Pagination
            className="justify-center"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}

        <ConfirmDialog
          open={deleteTargetId !== null}
          onOpenChange={(open) => !open && setDeleteTargetId(null)}
          iconVariant="neutral"
          icon={<img src={IconHelp} alt="Icon Alert Circle" />}
          title="관리자 삭제 확인"
          description="관리자 목록에서 박지민님을 영구적으로 삭제하시겠습니까?"
          primaryAction={{ label: "네", onClick: handleDeleteConfirm }}
          secondaryAction={{
            label: "아니요",
            onClick: () => setDeleteTargetId(null),
          }}
        />
      </div>
    </>
  );
};

export default AdminManagementListPage;
